import type { RunStatus } from "@/lib/api/extract";

export type DocumentKind = "fatture_ddt" | "polizza_carico";
export type RunHistoryKind = "all" | DocumentKind;
export type RunHistoryStatus = "all" | RunStatus;

export type RunHistoryQuery = {
  kind: RunHistoryKind;
  status: RunHistoryStatus;
  fileQuery: string;
  page: number;
};

export const RUN_HISTORY_PAGE_SIZE = 20;
export const DEFAULT_RUN_HISTORY_QUERY: RunHistoryQuery = {
  kind: "all",
  status: "all",
  fileQuery: "",
  page: 1,
};

export type UnifiedRunStatus = {
  kind: DocumentKind;
  id: number;
  status: RunStatus;
  inputFilename: string | null;
  renderDpiUsed: number | null;
  totalPages: number;
  processedPages: number;
  failedPages: number;
  currentPage: number | null;
  progressPct: number;
  startedAt: string | null;
  finishedAt: string | null;
  lastError: string | null;
};

export type UnifiedRunHistoryItem = {
  kind: DocumentKind;
  id: number;
  status: RunStatus;
  inputFilename: string | null;
  renderDpiUsed: number | null;
  totalPages: number;
  processedPages: number;
  failedPages: number;
  progressPct: number;
  startedAt: string | null;
  finishedAt: string | null;
  lastError: string | null;
  fatturePages?: number;
  ddtPages?: number;
  unknownPages?: number;
  correctFattureCount?: number;
  mismatchesCount?: number;
  reconciliationStatus?: string | null;
};

export type RunFailureScope = "document" | "page";

type RunErrorLike = Pick<
  UnifiedRunStatus | UnifiedRunHistoryItem,
  "kind" | "status" | "failedPages" | "lastError"
>;

type RunProgressLike = Pick<
  UnifiedRunStatus | UnifiedRunHistoryItem,
  "id" | "status" | "progressPct"
>;

export type RunFailurePresentation = {
  scope: RunFailureScope;
  title: string;
  message: string;
  nextStep: string | null;
  suggestedKind: DocumentKind | null;
};

export type RunHistoryOutcomeSummary = {
  label: string;
  detail: string | null;
  badgeTone: "default" | "warning";
};

export function normalizeDate(value: string | null): number {
  if (!value) return 0;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? 0 : parsed;
}

export function toKindLabel(kind: DocumentKind): string {
  return kind === "fatture_ddt" ? "Fatture + DDT" : "Polizza di carico";
}

export function toStatusLabel(status: RunStatus): string {
  if (status === "queued") return "in coda";
  if (status === "processing") return "in lavorazione";
  if (status === "completed") return "completato";
  if (status === "failed") return "fallito";
  return status;
}

function sanitizeErrorMessage(message: string): string {
  return message
    .replace(/\s*Usa\s+\/api\/extract-polizza[^.]*\./gi, "")
    .replace(/\s*Usa\s+\/api\/extract[^.]*\./gi, "")
    .replace(/\s+/g, " ")
    .trim();
}

export function getRunFailurePresentation(
  run: RunErrorLike | null,
): RunFailurePresentation | null {
  if (!run || !run.lastError) return null;

  const rawMessage = run.lastError.trim();
  if (!rawMessage) return null;

  const lowerMessage = rawMessage.toLowerCase();
  const sanitizedMessage = sanitizeErrorMessage(rawMessage);

  if (
    lowerMessage.includes("non una polizza di carico") ||
    lowerMessage.includes("sembra una fattura/ddt")
  ) {
    return {
      scope: "document",
      title: "Tipo documento non coerente",
      message:
        "Il file caricato sembra una fattura o un DDT, non una polizza di carico.",
      nextStep:
        "Apri il tab Carica e rilancia l'analisi con il workflow Fatture + DDT.",
      suggestedKind: "fatture_ddt",
    };
  }

  if (
    lowerMessage.includes("non una fattura/ddt") ||
    lowerMessage.includes("non una fattura / ddt") ||
    lowerMessage.includes("sembra una polizza di carico")
  ) {
    return {
      scope: "document",
      title: "Tipo documento non coerente",
      message:
        "Il file caricato sembra una polizza di carico, non una fattura o un DDT.",
      nextStep:
        "Apri il tab Carica e rilancia l'analisi con il workflow Polizza di carico.",
      suggestedKind: "polizza_carico",
    };
  }

  if (run.failedPages > 0) {
    return {
      scope: "page",
      title: "Analisi completata con errori",
      message: sanitizedMessage,
      nextStep:
        "Controlla le pagine fallite o riavvia l'analisi se il file era incompleto.",
      suggestedKind: null,
    };
  }

  return {
    scope: "document",
    title: "Elaborazione interrotta",
    message: sanitizedMessage,
    nextStep:
      "Riprova dal tab Carica oppure apri un'altra analisi dallo storico.",
    suggestedKind: null,
  };
}

export function getRunHeadline(run: RunProgressLike | null): string {
  if (!run) return "Nessuna analisi selezionata";

  const roundedProgress = Math.max(
    0,
    Math.min(100, Math.round(run.progressPct)),
  );

  if (run.status === "queued") return `Analisi #${run.id} · in coda`;
  if (run.status === "processing") {
    return `Analisi #${run.id} · ${roundedProgress}% completato`;
  }
  if (run.status === "completed") return `Analisi #${run.id} · completata`;
  if (roundedProgress > 0) {
    return `Analisi #${run.id} · interrotta al ${roundedProgress}%`;
  }
  return `Analisi #${run.id} · interrotta`;
}

export function getRunOutcomeSummary(run: RunErrorLike): {
  label: string;
  detail: string | null;
} {
  const failurePresentation = getRunFailurePresentation(run);

  if (failurePresentation?.scope === "document") {
    return {
      label: "Errore documento",
      detail: failurePresentation.title,
    };
  }

  if (run.failedPages > 0) {
    return {
      label:
        run.failedPages === 1
          ? "1 pagina fallita"
          : `${run.failedPages} pagine fallite`,
      detail:
        run.status === "failed"
          ? "L'elaborazione si e interrotta."
          : "Analisi completata con anomalie.",
    };
  }

  if (run.status === "failed") {
    return {
      label: "Analisi fallita",
      detail:
        failurePresentation?.title ?? "Verifica il dettaglio dell'errore.",
    };
  }

  return {
    label: "Nessun errore",
    detail: null,
  };
}

export function getRunHistoryOutcomeSummary(
  run: UnifiedRunHistoryItem,
): RunHistoryOutcomeSummary {
  if (run.kind === "fatture_ddt" && run.status === "completed") {
    const mismatchesCount = Math.max(0, run.mismatchesCount ?? 0);

    return {
      label:
        mismatchesCount > 0
          ? `Mismatch: ${mismatchesCount}`
          : "Nessun mismatch",
      detail: null,
      badgeTone: mismatchesCount > 0 ? "warning" : "default",
    };
  }

  const outcome = getRunOutcomeSummary(run);
  return {
    ...outcome,
    badgeTone: "default",
  };
}
